import { OverflowMenuHorizontal } from '@carbon/icons-react';
import { Fragment, useState } from 'react';
import { View } from 'react-native';
import type { Datastore } from 'util/datastore';
import { useDatastore } from 'util/datastore';
import type { TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

import { HorizBox, Pad } from './basics';
import type { ButtonType } from './button';
import { ActionButton, TextButton } from './button';
import { FunctionalModal } from './modal';
import { Popup } from './popup';

type ActionsOverlayModalSettings = {
    headingLabel?: TLabel;
    headingText?: string;
    descriptionLabel?: TLabel;
    descriptionText?: string;
    confirmButton: {
        label?: TLabel;
        text?: string;
        type?: ButtonType;
        icon?: JSX.Element;
    };
};

export type ActionsOverlayAction<T = {}> = {
    label: TLabel;
    icon: React.ComponentType<any>;
    color?: string;
    modal?: ActionsOverlayModalSettings;
    action: (args: { datastore: Datastore } & T) => void;
    enabled?: (args: { features: Record<string, any> }) => boolean;
};

type ActionsOverlayProps<T> = {
    actions: ActionsOverlayAction<T>[];
    actionArgs?: T;
    alignRight?: boolean;
    testID?: string;
};
// eslint-disable-next-line max-lines-per-function
export function ActionsOverlay<T>({ actions, actionArgs, alignRight = true, testID = 'actions-overlay' }: ActionsOverlayProps<T>) {
    const datastore = useDatastore();
    const [hover, setHover] = useState(false);
    const [modalActionIndex, setModalActionIndex] = useState<number | null>(null);

    const popupContent = ({ onClose }: { onClose: () => void }) => {
        return (
            <View style={{ minWidth: 100 }}>
                {actions.map((action, index) => (
                    <Fragment key={index}>
                        <TextButton
                            color={action.color}
                            label={action.label}
                            leftIcon={action.icon}
                            onPress={() => {
                                if (action.modal) {
                                    setModalActionIndex(index);
                                } else {
                                    action.action({ datastore, ...(actionArgs as T) });
                                }
                                onClose();
                            }}
                            type='small'
                        />
                        {index < actions.length - 1 && <Pad size={20} />}
                    </Fragment>
                ))}
            </View>
        );
    };
    return (
        <HorizBox right={alignRight}>
            <Popup onClose={() => {}} popupContent={popupContent} setHover={setHover} testID={testID}>
                <ActionButton hover={hover} icon={OverflowMenuHorizontal} />
            </Popup>
            {modalActionIndex !== null && actions[modalActionIndex] && (
                <FunctionalModal
                    confirmButton={{
                        label: actions[modalActionIndex]?.modal?.confirmButton?.label,
                        type: actions[modalActionIndex]?.modal?.confirmButton?.type,
                        icon: actions[modalActionIndex]?.modal?.confirmButton?.icon,
                    }}
                    descriptionLabel={actions[modalActionIndex]?.modal?.descriptionLabel}
                    descriptionText={actions[modalActionIndex]?.modal?.descriptionText}
                    headingLabel={actions[modalActionIndex]?.modal?.headingLabel}
                    headingText={actions[modalActionIndex]?.modal?.headingText}
                    onClose={() => setModalActionIndex(null)}
                    onConfirm={() => {
                        actions[modalActionIndex]?.action({ datastore, ...(actionArgs as T) });
                        setModalActionIndex(null);
                    }}
                />
            )}
        </HorizBox>
    );
}
